import { useState, useEffect, useMemo, useContext } from 'react';
import stringify from 'json-stable-stringify';
import _ from 'underscore';

// Move legacy deps and data reading out from UI component
import edm from '@bingads-webui-component-legacy/edm-campaign/index';
import {
  getFilteredTotalsMetrics,
  availableGranularities,
  fetchAggregatedData,
  allMetrics,
  metricToDisplayNameMap,
  AccountsMode,
} from '@bingads-webui-campaign/overview-cards-common';

import dateUtil from '@bingads-webui-component-legacy/humanize/date';
import { fetchPerformanceTimeSeries } from '@bingads-webui-campaign-legacy/performance-graph/data-fetch';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import config from '@bingads-webui-component-legacy/config/index';
import { formatMetric } from '@bingads-webui-campaign-legacy/performance-metric-formatter/index';
import legacyPreference from '@bingads-webui-component-legacy/preference/service/index';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { cardPerformanceId } from '@bingads-webui-campaign/fluent-page-consts';

import {
  getNavigationLevel,
  NavigationLevel,
  useIsPreferenceInitialized,
} from '@bingads-webui-campaign/fluent-page-data';


export function useData({
  dateRange,
  filter,
  i18n,
  initialSelectedMetricIndexesFromPreference,
  initialMetricsFromPreference,
  getCardsConfig,
  sesId,
  activity,
  willRender,
  willFetchData,
  campaignType,
  constructOptions,
}) {
  const [selectedMetricIndexes, setSelectedMetricIndexes] = useState([]);
  const [visibleMetrics, setVisibleMetrics] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [aggregatedMetricPickerData, setAggregatedMetricPickerData] = useState([]);
  const [hideCurrency, setHideCurrency] = useState(false);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [isMetricLoading, setIsMetricLoading] = useState(false);
  const [isFirstDataLoaded, setIsFirstDataLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { scope } = useContext(ScopeContext);

  const navigationLevel = useMemo(() => getNavigationLevel(scope.values), [scope.values]);
  const granularity = useMemo(() => availableGranularities(dateRange, dateUtil)[0], [dateRange]);
  const isPreferenceInitialized = useIsPreferenceInitialized(legacyPreference);
  useEffect(() => {
    if (isPreferenceInitialized && _.isEmpty(visibleMetrics)) {
      setSelectedMetricIndexes(initialSelectedMetricIndexesFromPreference);
      setVisibleMetrics(initialMetricsFromPreference);
    }
  }, [isPreferenceInitialized, visibleMetrics, initialSelectedMetricIndexesFromPreference, initialMetricsFromPreference]);

  const isLoading = isChartLoading || isMetricLoading;
  const dateString = stringify(dateRange);
  const filterString = stringify(filter);

  const entitySet = useMemo(() => {
    if (constructOptions) {
      return constructOptions({
        campaignType,
        navigationLevel,
      }).entitySet;
    }

    // Now Pmax Campaign Overview page is using this path without constructOptions
    // After we merge Pmax Campaign Overview page into our new page, we can remove this.
    return edm.currentAccount.Campaigns;
  }, [campaignType, constructOptions, navigationLevel]);

  const params = new URLSearchParams(document.location.search);
  const campaignId = params.get('campaignId');
  const extendedFilter = useMemo(() => {
    let extraFilter = [];
    if (constructOptions) {
      ({ extraFilter = [] } = constructOptions({
        campaignType,
        navigationLevel,
      }));
    // Now Pmax Campaign Overview page is using this path without constructOptions
    // After we merge Pmax Campaign Overview page into our new page, we can remove this.
    } else if (campaignId) {
      extraFilter = [{
        filter: {
          key: 'CampaignId',
          value: {
            query: {
              Id: Number(campaignId),
            },
          },
        },
      }];
    }

    let baseFilter = filter || [];
    if (campaignId) {
      // If we have campaign Id in query, we should not apply any campaign status filter anymore.
      baseFilter = baseFilter.filter(item => _.isEmpty(_.result(item, ['filter', 'value', 'query', 'CampaignStatus'])));
    }
    return [
      ...baseFilter,
      ...extraFilter,
    ];
    // we cannot check object filter here, because it will always be a different object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, constructOptions, filterString]);
  const fetchSingleTile = navigationLevel === NavigationLevel.Campaign;

  useEffect(() => {
    if (dateRange && filter && !isLoading) {
      setIsChartLoading(true);
      setIsMetricLoading(true);
      setHasError(false);
      setAggregatedMetricPickerData([]);
      activity.trace(`Load fluent perf chart data with new date and filter value: date: ${dateString} filter: ${filterString}`);
      willFetchData();
      fetchPerformanceTimeSeries({
        entitySet,
        movingAverage: false,
        period: dateRange,
        filter: extendedFilter,
        campaignType,
        granularity,
        navigationLevel,
        // TODO: this is used for account level call, we need to handle this in the future
        getCardsConfig,
        fetchSingleTile,
        accountsMode: 1,
        dfmCacheKey: `${cardPerformanceId}-time-series`,
      }).then((data) => {
        // This interface is weird, it still resolves if there is an error
        if (!data || !data.performanceTimeSeries) {
          setHasError(true);
          activity.error(`Load fluent perf chart data error: ${JSON.stringify(data)}`);
          return;
        }

        setPerformanceData(data.performanceTimeSeries);
      }).finally(() => {
        setIsChartLoading(false);
        setIsFirstDataLoaded(true);
        willRender();
      }).catch((e) => {
        setHasError(true);
        activity.error(`Load fluent perf chart data error: ${JSON.stringify(e)}`);
      });

      const permissions = config.get('Permissions');
      fetchAggregatedData({
        entitySet,
        dateRange,
        filter: extendedFilter,
        jsdata,
        dateUtil,
        navigationLevel,
        getCardsConfig,
        fetchSingleTile,
        accountsMode: 1,
        permissions,
        dfmCacheKey: `${cardPerformanceId}-aggregated-data`,
        campaignType,
      }).then((data) => {
        // This interface is weird, it still resolves if there is an error
        if (!data || (!data.raw && !data.OverallTotals)) {
          setHasError(true);
          activity.error(`Load fluent perf chart aggregated data error: ${JSON.stringify(data)}`);
          return;
        }
        const totalMetrics = getFilteredTotalsMetrics({
          data,
          metric: 'PerformanceMetrics',
        }) || {};
        const availableMetricKeys = allMetrics(permissions, AccountsMode.Expert, campaignType);
        const availableTotalMetrics = _.pick(totalMetrics, availableMetricKeys);
        setHideCurrency(data.AccountsHaveDifferentCurrencies);
        const metricPickerData = Object.keys(availableTotalMetrics).map((key) => {
          const metric = totalMetrics[key];
          return {
            key,
            total: formatMetric(
              key,
              metric || 0,
              { hideCurrency: data.AccountsHaveDifferentCurrencies }
            ),
            displayName: i18n.getString(metricToDisplayNameMap[key]),
          };
        });
        setAggregatedMetricPickerData(metricPickerData);
      }).finally(() => {
        setIsMetricLoading(false);
      }).catch((e) => {
        setHasError(true);
        activity.error(`Load fluent perf chart aggregated data error: ${JSON.stringify(e)}`);
      });
    }

    // we only update when dateString, filterString, sesId change
    // we cannot check object dateRange and filter here, because they will always be a different object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateString, filterString, sesId, campaignType]);

  return {
    selectedMetricIndexes,
    setSelectedMetricIndexes,
    visibleMetrics,
    setVisibleMetrics,
    performanceData,
    aggregatedMetricPickerData,
    hideCurrency,
    granularity,
    isLoading,
    isFirstDataLoaded,
    hasError,
  };
}
